import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { usePagination } from "hooks";
import { AMBIENT_SCRIBE_PAUSED_FEATURE } from "constants/feature-flags";
import { orderBy } from "lodash";
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  setOnChange,
  setOpen,
  setTitle
} from "components/confirm-dialog/confirm-dialog.slice";
import type { CustomFilterOption } from "components/custom-filters-list/custom-filters-list.component";
import { selectCurrentClinic, selectCurrentUser } from "features/auth/auth-slice";
import { AMBIENT_SCRIBE_REFETCH } from "hooks/use-event-bus/event-bus";
import { useEventBus } from "hooks/use-event-bus/use-event-bus.hook";
import {
  useCreateAmbientScribeMutation,
  useDeleteAmbientScribeMutation,
  useGetAmbientScribesQuery
} from "services/ambient-scribe";
import type {
  AmbientScribe,
  AmbientScribeFilters
} from "services/types/ambient-scribe.type";
import { getFullName } from "utils/common";

import {
  AMBIENT_SCRIBE_BACKEND_STATUS_CANCELLED,
  AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED,
  AMBIENT_SCRIBE_BACKEND_STATUS_FAILED,
  AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
  AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING,
  AMBIENT_SCRIBE_BACKEND_STATUS_RETRYING,
  AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED,
  AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED,
  AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED
} from "../medical-scribe-constants";
import type { RecordingData } from "../medical-scribe-db/index-db-types.type";
import {
  deleteChunksById,
  deleteRecordingById,
  getAllRecordings,
  getChunkById,
  getDB,
  getRecordingById,
} from "../medical-scribe-db/medical-scribe-db.util";
import { getAmbientScribeStatus, mergeChunks } from "../utils";
import type { MedicalScribe } from "./medical-scribes.type";
import useScribeCustomFilterOptions, {
  SCRIBES_DEFAULT_FILTERS,
  type ScribesCustomCustomFiltersValues
} from "./scribe-filters/use-scribe-custom-filter-options.hook";
import { formatSecondsToHHMMSS } from "./scribes-list/utils";

type DateRange = {
  after_date: string | null;
  before_date: string | null;
};

const MedicalScribeSectionContext = React.createContext<
  | ({
      localRecords: RecordingData[];
      scribes: MedicalScribe[];
      fetchMore: () => void;
      hasMoreData: boolean;
      isLoading: boolean;
      refetch: () => void;
      updateDateRange: React.Dispatch<React.SetStateAction<DateRange>>;
      uploadRecording: (row: MedicalScribe) => void;
      dialogDetails: MedicalScribe | undefined;
      setDialogDetails: React.Dispatch<
        React.SetStateAction<MedicalScribe | undefined>
      >;
      deleteRecording: (row: MedicalScribe) => void;
      fetchLocalRecords: () => Promise<void>;
      isBusy: boolean;
      uploadingLocalId: string | undefined;
      currentFilters: AmbientScribeFilters;
      customFilters: CustomFilterOption[];
      selectedCustomFilters: ScribesCustomCustomFiltersValues;
      updateCustomFilter: (field: string, value: any) => void;
      setSelectedCustomFilters: React.Dispatch<
        React.SetStateAction<ScribesCustomCustomFiltersValues>
      >;
    } & DateRange)
  | null
>(null);

type MedicalScribeSectionProviderProps = {
  children?: React.ReactNode;
  patientId?: string | number;
};

export function MedicalScribeSectionProvider({
  children,
  patientId
}: MedicalScribeSectionProviderProps) {
  const flags = useFlags();
  const isPausedFeatureEnabled = flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false;
  const dispatch = useAppDispatch();
  const currentClinic = useAppSelector(selectCurrentClinic);
  const currentUser = useAppSelector(selectCurrentUser);

  const [localRecords, setLocalRecords] = useState<RecordingData[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadingLocalId, setUploadingLocalId] = useState<string | undefined>(
    undefined
  );

  const fetchLocalRecords = useCallback(() => {
    return getAllRecordings().then((records) => {
      const result = orderBy(records, "id", "desc");
      setLocalRecords(result);
    });
  }, []);

  // Initialize with patientId if provided to avoid first-render unfiltered API call
  const [currentFilters, setCurrentFilters] = useState<AmbientScribeFilters>(
    patientId ? { patient: patientId } : {}
  );

  const [selectedCustomFilters, setSelectedCustomFilters] =
    useState<ScribesCustomCustomFiltersValues>(SCRIBES_DEFAULT_FILTERS);

  const updateCustomFilter = useCallback(
    (field: string, value: any) => {
      return setCurrentFilters((prevFilters) => ({
        ...prevFilters,
        [field]: value
      }));
    },
    [setCurrentFilters]
  );

  const customFilters = useScribeCustomFilterOptions({
    updateCustomFilter,
    selectedCustomFilters,
    setSelectedCustomFilters
  });

  useEffect(() => {
    setCurrentFilters({
      staff: selectedCustomFilters?.staff?.value,
      clinic: selectedCustomFilters?.clinic?.value,
      // Lock patient to prop when provided
      patient: patientId ?? selectedCustomFilters.patient?.value
    });
  }, [selectedCustomFilters, patientId]);

  const filters = useMemo(() => {
    const status = [
      AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING,
      AMBIENT_SCRIBE_BACKEND_STATUS_UPLOADED,
      AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
      AMBIENT_SCRIBE_BACKEND_STATUS_TRANSCRIBED,
      AMBIENT_SCRIBE_BACKEND_STATUS_COMPLETED,
      AMBIENT_SCRIBE_BACKEND_STATUS_FAILED,
      AMBIENT_SCRIBE_BACKEND_STATUS_RETRYING,
      AMBIENT_SCRIBE_BACKEND_STATUS_CANCELLED
    ];
    if (isPausedFeatureEnabled) {
      status.push(AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED);
    }
    return {
      ...currentFilters,
      status
    };
  }, [currentFilters, isPausedFeatureEnabled]);

  const {
    data = [],
    fetchMore,
    hasMoreData,
    isLoading: isPaginationLoading,
    isFetching,
    refetch: refetchRemote,
    clearData
  } = usePagination<AmbientScribeFilters, AmbientScribe>({
    useQuery: useGetAmbientScribesQuery,
    limit: 25,
    params: filters,
    options: {
      skip: !currentClinic?.id,
      refetchOnMountOrArgChange: true
    }
  });

  const remoteData = data;

  const scribes = useMemo(() => {
    const result: MedicalScribe[] = [];

    const remoteRecordsMap = new Map<string, AmbientScribe>();
    const localRecordsMap = new Map<string, RecordingData>();

    // Filter local records by patientId when scoped to a patient (Bug 1 fix)
    const effectiveLocalRecords = patientId
      ? localRecords.filter((r) => String(r.patientId) === String(patientId))
      : localRecords;

    remoteData.forEach((scribe) => {
      const { created_at, duration, patient, staff } = scribe;
      const practiceId = String(currentClinic?.practice?.id ?? 0);

      const key = [
        new Date(created_at ?? "").toISOString(),
        practiceId,
        staff?.id,
        patient?.id,
        duration ?? "-"
      ].join("-");

      if (!remoteRecordsMap.has(key)) {
        remoteRecordsMap.set(key, scribe);
      }
    });

    effectiveLocalRecords
      .filter((record) => record.created_at)
      .forEach((record) => {
        const { created_at, patientId, userId, duration } = record;

        const practiceId = String(currentClinic?.practice?.id ?? 0);
        const formattedDuration = duration
          ? formatSecondsToHHMMSS(duration)
          : "";

        const key = [
          new Date(created_at ?? "").toISOString(),
          practiceId,
          userId,
          patientId,
          formattedDuration ?? "-"
        ].join("-");

        if (!localRecordsMap.has(key)) {
          localRecordsMap.set(key, record);
        }
      });

    const localSet = new Set(localRecordsMap.keys());
    const remoteSet = new Set(remoteRecordsMap.keys());

    //add intersected records
    const intersectedIds = [
      ...new Set([...localSet].filter((key) => remoteSet.has(key)))
    ];

    intersectedIds.forEach((id) => {
      const localRecord = localRecordsMap.get(id);
      const remoteRecord = remoteRecordsMap.get(id);

      if (localRecord && remoteRecord) {
        result.push({
          id: Number(remoteRecord?.id || 0),
          local_id: localRecord.id,
          uploadStarted: localRecord.uploadStarted,
          has_transcription: Boolean(remoteRecord.has_transcription),
          duration: remoteRecord.duration ?? "00:00:00",
          recording_url: remoteRecord.recording,
          uploaded_at: new Date(remoteRecord?.created_at ?? "").toISOString(),
          created_at: new Date(localRecord?.startTime ?? "").toISOString(),
          staffName: remoteRecord.staff
            ? `${getFullName({
                firstName: remoteRecord.staff.first_name,
                lastName: remoteRecord.staff.last_name,
                title: remoteRecord.staff.title,
                suffix: remoteRecord.staff.suffix
              })}`
            : currentUser?.fullName,
          patient: {
            id: Number(remoteRecord.patient?.id),
            fullName:
              remoteRecord.patient && typeof remoteRecord.patient === "object"
                ? getFullName({
                    firstName: remoteRecord.patient?.first_name,
                    lastName: remoteRecord.patient?.last_name,
                    middleName: remoteRecord.patient?.middle_name,
                    preferredName: remoteRecord.patient?.preferred_name,
                    title: remoteRecord.patient?.title,
                    suffix: remoteRecord.patient?.suffix
                  })
                : localRecord.patientFullName
          },
          status: remoteRecord.status
        });
      }
    });

    //add local only records
    const localOnlyKeys = new Set(
      [...localSet].filter((key) => !remoteSet.has(key))
    );

    localOnlyKeys.forEach((id) => {
      const localRecord = localRecordsMap.get(id);

      // Skip streaming records - they should only show when matched with remote records
      if (localRecord && !localRecord.isStreaming) {
        const createdAt = new Date(localRecord?.startTime ?? "").toISOString();
        result.push({
          local_id: localRecord.id,
          uploadStarted: localRecord.uploadStarted,
          has_transcription: false,
          duration: localRecord.duration
            ? formatSecondsToHHMMSS(Number(localRecord.duration))
            : "00:00",

          uploaded_at: createdAt,
          created_at: createdAt,
          staffName: currentUser?.fullName,
          patient: {
            id: localRecord.patientId,
            fullName: localRecord.patientFullName
          },
          status: getAmbientScribeStatus(localRecord)
        });
      }
    });

    //add remote only records
    const remoteOnlyKeys = new Set(
      [...remoteSet].filter((key) => !localSet.has(key))
    );

    remoteOnlyKeys.forEach((id) => {
      const remoteRecord = remoteRecordsMap.get(id);

      if (remoteRecord) {
        result.push({
          id: Number(remoteRecord?.id || 0),
          duration: remoteRecord.duration ?? "00:00",
          recording_url: remoteRecord.recording,
          has_transcription: Boolean(remoteRecord.has_transcription),
          uploaded_at: new Date(remoteRecord?.created_at ?? "").toISOString(),
          created_at: new Date(remoteRecord?.created_at ?? "").toISOString(),
          staffName: `${getFullName({
            firstName: remoteRecord.staff.first_name,
            lastName: remoteRecord.staff.last_name,
            title: remoteRecord.staff.title,
            suffix: remoteRecord.staff.suffix
          })}`,
          status: remoteRecord.status,
          patient: {
            id: Number(remoteRecord.patient?.id),
            fullName:
              remoteRecord.patient && typeof remoteRecord.patient === "object"
                ? getFullName({
                    firstName: remoteRecord.patient?.first_name,
                    lastName: remoteRecord.patient?.last_name,
                    middleName: remoteRecord.patient?.middle_name,
                    preferredName: remoteRecord.patient?.preferred_name,
                    title: remoteRecord.patient?.title,
                    suffix: remoteRecord.patient?.suffix
                  })
                : ""
          }
        });
      }
    });

    return orderBy(result, "created_at", "desc");
  }, [
    currentClinic?.practice?.id,
    currentUser?.fullName,
    remoteData,
    localRecords,
    patientId
  ]);

  const [{ after_date, before_date }, updateDateRange] = useState<DateRange>({
    before_date: null,
    after_date: null
  });

  const refetch = useCallback(async () => {
    fetchLocalRecords().then(() => {
      clearData();
      refetchRemote();
    });
  }, [fetchLocalRecords, refetchRemote, clearData]);

  const [dialogDetails, setDialogDetails] = useState<MedicalScribe | undefined>(
    undefined
  );

  const [deleteAmbientScribe, { isLoading: isDeleting }] =
    useDeleteAmbientScribeMutation();

  const [createAmbientScribe, { isLoading: isCreating }] =
    useCreateAmbientScribeMutation();

  const deleteRecordingConfirmed = useCallback(
    (row: MedicalScribe) => {
      if (!row.id) {
        return deleteRecordingById(String(row.local_id)).then(() => {
          refetch();
        });
      } else {
        return deleteAmbientScribe(Number(row.id)).then(() => {
          deleteRecordingById(String(row.local_id)).then(() => {
            refetch();
          });
        });
      }
    },
    [deleteAmbientScribe, refetch]
  );

  const deleteRecording = useCallback(
    (row: MedicalScribe) => {
      dispatch(setTitle("Are you sure you want to delete audio recording?"));
      dispatch(
        setOnChange(() => {
          deleteRecordingConfirmed(row);
        })
      );
      dispatch(setOpen(true));
    },
    [deleteRecordingConfirmed, dispatch]
  );

  const uploadRecording = useCallback(
    async (row: MedicalScribe) => {
      const localId = String(row?.local_id ?? "");
      if (!localId) return;

      setIsUploading(true);
      setUploadingLocalId(localId);

      let markedUploadingRecord: RecordingData | null = null;
      try {
        const chunkResult = await getChunkById(localId);
        if (!chunkResult?.id) {
          throw new Error("No local audio chunks found for upload.");
        }

        const blob = mergeChunks(chunkResult.chunks);
        if (!blob || blob.size === 0) {
          dispatch(
            setCustomMessage(
              "Error: Audio recording is empty or invalid. Please try recording again."
            )
          );
          dispatch(setSnackbarOpen(true));
          return;
        }

        if (blob.size < 100) {
          console.warn(
            `Audio blob is very small (${blob.size} bytes), may be incomplete`
          );
        }

        const recording = await getRecordingById(localId);
        if (
          !recording ||
          recording.uploadStatus === "uploading" ||
          recording.uploadStarted
        ) {
          return;
        }

        const now = Date.now();
        markedUploadingRecord = {
          ...recording,
          uploadStatus: "uploading",
          uploadStarted: now
        };

        const db = await getDB();
        const markTx = db.transaction("recordings", "readwrite");
        await markTx.objectStore("recordings").put(markedUploadingRecord);
        await markTx.done;

        const audio_file = new File([blob], uuidv4(), { type: blob.type });
        const response = await createAmbientScribe({
          patient: Number(row.patient.id),
          duration: row.duration,
          practice: Number(currentClinic?.practice?.id),
          staff: Number(currentUser?.id),
          audio_file,
          clinic: currentClinic?.id
        });

        if ("error" in response || !response.data?.id) {
          if (markedUploadingRecord) {
            const failTx = db.transaction("recordings", "readwrite");
            await failTx.objectStore("recordings").put({
              ...markedUploadingRecord,
              uploadStatus: "failed",
              uploadStarted: 0
            });
            await failTx.done;
          }
          dispatch(
            setCustomMessage("An Error has Occurred, please reach out to support.")
          );
          dispatch(setSnackbarOpen(true));
          return;
        }

        const remoteId = Number(response.data.id);
        const remoteCreatedAt = response.data.created_at;

        const successTx = db.transaction("recordings", "readwrite");
        await successTx.objectStore("recordings").put({
          ...markedUploadingRecord,
          uploadStatus: "uploaded",
          uploadStarted: 0,
          uploaded: now,
          medical_transcribe_id: remoteId,
          created_at: remoteCreatedAt
        });
        await successTx.done;

        // Audio chunks are no longer needed after successful upload.
        await deleteChunksById(localId);
        await refetch();

        dispatch(setCustomMessage("Transcription completed."));
        dispatch(setSnackbarOpen(3000));
      } catch (error) {
        if (markedUploadingRecord) {
          try {
            const db = await getDB();
            const failTx = db.transaction("recordings", "readwrite");
            await failTx.objectStore("recordings").put({
              ...markedUploadingRecord,
              uploadStatus: "failed",
              uploadStarted: 0
            });
            await failTx.done;
          } catch (markFailedError) {
            console.error("Failed to mark recording upload as failed", markFailedError);
            // Keep original upload error as primary signal.
          }
        }
        dispatch(
          setCustomMessage("An Error has Occurred, please reach out to support.")
        );
        dispatch(setSnackbarOpen(true));
      } finally {
        setIsUploading(false);
        setUploadingLocalId(undefined);
      }
    },
    [
      createAmbientScribe,
      currentClinic?.practice?.id,
      currentClinic?.id,
      currentUser?.id,
      dispatch,
      refetch
    ]
  );

  useEventBus(AMBIENT_SCRIBE_REFETCH, () => {
    refetch();
  });

  const value = useMemo(
    () => ({
      localRecords,
      scribes,
      fetchMore,
      hasMoreData,
      isLoading: isFetching || isPaginationLoading,
      refetch,
      after_date,
      before_date,
      updateDateRange,
      uploadRecording,
      dialogDetails,
      setDialogDetails,
      deleteRecording,
      fetchLocalRecords,
      isBusy: isDeleting || isCreating || isUploading,
      uploadingLocalId,
      currentFilters,
      setCurrentFilters,
      selectedCustomFilters,
      customFilters,
      updateCustomFilter,
      setSelectedCustomFilters
    }),
    [
      localRecords,
      scribes,
      fetchMore,
      hasMoreData,
      isFetching,
      isPaginationLoading,
      refetch,
      after_date,
      before_date,
      uploadRecording,
      dialogDetails,
      deleteRecording,
      fetchLocalRecords,
      isDeleting,
      isCreating,
      isUploading,
      uploadingLocalId,
      currentFilters,
      selectedCustomFilters,
      customFilters,
      updateCustomFilter,
      setSelectedCustomFilters
    ]
  );

  return (
    <MedicalScribeSectionContext.Provider value={value}>
      {children}
    </MedicalScribeSectionContext.Provider>
  );
}

export default function useMedicalScribeSection() {
  const context = React.useContext(MedicalScribeSectionContext);
  if (!context) {
    throw new Error(
      `useMedicalScribeSection must be used within a MedicalScribeSectionProvider`
    );
  }

  return context;
}
